import React from 'react';
import {
  Box,
  Container,
  SimpleGrid,
  Stack,
  Text,
  Flex,
  Heading,
  Link,
  Divider,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import OptimizedImage from '../common/OptimizedImage';

// Cyan-gradient rounded square with a white line icon (matches design)
const IconChip = ({ children, mt = 0 }) => (
  <Flex
    flexShrink={0}
    w="20px"
    h="20px"
    mt={mt}
    borderRadius="full"
    align="center"
    justify="center"
    bg="rgba(255,255,255,0.06)"
    border="1px solid rgba(255,255,255,0.16)"
  >
    {children}
  </Flex>
);

const PinIcon = () => (
  <Box as="svg" width="11" height="11" viewBox="0 0 24 24" fill="none">
    <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z" stroke="#fff" strokeWidth="2.4" strokeLinejoin="round" />
  </Box>
);

const PhoneIcon = () => (
  <Box as="svg" width="11" height="11" viewBox="0 0 24 24" fill="none">
    <path d="M5 4h4l2 5-3 2a12 12 0 0 0 5 5l2-3 5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
  </Box>
);

const MailIcon = () => (
  <Box as="svg" width="11" height="11" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="#fff" strokeWidth="2" />
    <path d="M4 7l8 6 8-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Box>
);

const ClockIcon = () => (
  <Box as="svg" width="11" height="11" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2.2" />
    <path d="M12 7v5l3 2" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </Box>
);

const Footer = () => {
  return (
    <Box
      bg="#0A2029"
      color="white"
      position="relative"
      overflow="hidden"
      mt="auto"
    >
      {/* A single hairline to separate the footer, in place of a cyan-to-blue
          gradient strip. */}
      <Box h="1px" bg="rgba(255,255,255,0.08)" />

      <Container maxW="container.xl" py={16} position="relative">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={10}>
          {/* Column 1: Brand + logo */}
          <Stack spacing={4}>
            <Box>
              <OptimizedImage
                src="/logo-lucien.png"
                alt="Lake Lucien Surgery Center"
                h="52px"
                objectFit="contain"
                filter="brightness(0) invert(1)"
                mb={1}
              />
            </Box>
            <Text fontSize="sm" color="gray.400" lineHeight="1.7" maxW="290px">
              Excellence in ambulatory surgical care, combining cutting-edge technology
              with compassionate patient-centered treatment.
            </Text>
            <Box w="40px" h="2px" bg="starOfLife.400" borderRadius="full" mt={2} />
          </Stack>

          {/* Column 2: Quick Links */}
          <Stack spacing={4}>
            <Heading size="sm" fontWeight="600" color="white" textTransform="uppercase" letterSpacing="0.14em">
              Quick Links
            </Heading>
            <Stack spacing={2}>
              <Link
                as={RouterLink}
                to="/"
                fontSize="sm"
                color="gray.400"
                _hover={{ color: 'starOfLife.300', textDecoration: 'none', pl: 2 }}
                transition="all 0.3s"
              >
                Home
              </Link>
              <Link
                as={RouterLink}
                to="/about-team"
                fontSize="sm"
                color="gray.400"
                _hover={{ color: 'starOfLife.300', textDecoration: 'none', pl: 2 }}
                transition="all 0.3s"
              >
                About Team
              </Link>
              <Link
                as={RouterLink}
                to="/about-facility"
                fontSize="sm"
                color="gray.400"
                _hover={{ color: 'starOfLife.300', textDecoration: 'none', pl: 2 }}
                transition="all 0.3s"
              >
                About Facility
              </Link>
              <Link
                as={RouterLink}
                to="/about-team#board"
                fontSize="sm"
                color="gray.400"
                _hover={{ color: 'starOfLife.300', textDecoration: 'none', pl: 2 }}
                transition="all 0.3s"
              >
                Board of Directors
              </Link>
              <Link
                as={RouterLink}
                to="/surgeons"
                fontSize="sm"
                color="gray.400"
                _hover={{ color: 'starOfLife.300', textDecoration: 'none', pl: 2 }}
                transition="all 0.3s"
              >
                Participating Surgeons
              </Link>
              <Link
                as={RouterLink}
                to="/specialties"
                fontSize="sm"
                color="gray.400"
                _hover={{ color: 'starOfLife.300', textDecoration: 'none', pl: 2 }}
                transition="all 0.3s"
              >
                Specialties
              </Link>
              <Link
                as={RouterLink}
                to="/price-transparency"
                fontSize="sm"
                color="accent.300"
                fontWeight="700"
                _hover={{ color: 'accent.200', textDecoration: 'none', pl: 2 }}
                transition="all 0.3s"
              >
                Price Transparency
              </Link>
              <Link
                as={RouterLink}
                to="/contact"
                fontSize="sm"
                color="gray.400"
                _hover={{ color: 'starOfLife.300', textDecoration: 'none', pl: 2 }}
                transition="all 0.3s"
              >
                Contact
              </Link>
            </Stack>
          </Stack>

          {/* Column 3: Compliance & Legal */}
          <Stack spacing={4}>
            <Heading size="sm" fontWeight="600" color="white" textTransform="uppercase" letterSpacing="0.14em">
              Compliance
            </Heading>
            <Stack spacing={2}>
              <Link
                as={RouterLink}
                to="/legal/hipaa-baa"
                fontSize="sm"
                color="gray.400"
                _hover={{ color: 'starOfLife.300', textDecoration: 'none', pl: 2 }}
                transition="all 0.3s"
              >
                HIPAA BAA
              </Link>
              <Link
                as={RouterLink}
                to="/legal/data-processing-addendum"
                fontSize="sm"
                color="gray.400"
                _hover={{ color: 'starOfLife.300', textDecoration: 'none', pl: 2 }}
                transition="all 0.3s"
              >
                Data Processing
              </Link>
              <Text fontSize="xs" color="gray.500" mt={2}>
                HIPAA Compliant
              </Text>
            </Stack>
          </Stack>

          {/* Column 4: Contact Info */}
          <Stack spacing={4}>
            <Heading size="sm" fontWeight="600" color="white" textTransform="uppercase" letterSpacing="0.14em">
              Contact Us
            </Heading>
            <Stack spacing={3}>
              <Flex align="start" gap={3}>
                <IconChip mt={0.5}><PinIcon /></IconChip>
                <Text fontSize="sm" color="gray.400" lineHeight="1.6">
                  2200 Lucien Way, Suite 100<br />
                  Maitland, FL 32751
                </Text>
              </Flex>
              <Flex align="center" gap={3}>
                <IconChip><PhoneIcon /></IconChip>
                <Link href="tel:+13213213935" fontSize="sm" color="gray.400" _hover={{ color: 'starOfLife.300' }}>
                  (321) 321-3935
                </Link>
              </Flex>
              <Flex align="center" gap={3}>
                <IconChip><MailIcon /></IconChip>
                <Link href="mailto:info@lucienasc.com" fontSize="sm" color="gray.400" _hover={{ color: 'starOfLife.300' }}>
                  info@lucienasc.com
                </Link>
              </Flex>
            </Stack>
          </Stack>

          {/* Column 5: Hours */}
          <Stack spacing={4}>
            <Heading size="sm" fontWeight="600" color="white" textTransform="uppercase" letterSpacing="0.14em">
              Hours
            </Heading>
            <Flex align="start" gap={3}>
              <IconChip mt={0.5}><ClockIcon /></IconChip>
              <Stack spacing={1}>
                <Text fontSize="sm" color="gray.400">
                  <Text as="span" fontWeight="600" color="white">Mon - Thu:</Text> 7:00 AM - 4:00 PM
                </Text>
                <Text fontSize="sm" color="gray.400">
                  <Text as="span" fontWeight="600" color="white">Fri:</Text> 7:00 AM - 4:00 PM
                </Text>
                <Text fontSize="sm" color="gray.400">
                  <Text as="span" fontWeight="600" color="white">Sat - Sun:</Text> Closed
                </Text>
              </Stack>
            </Flex>
          </Stack>
        </SimpleGrid>
      </Container>

      {/* Bottom Bar */}
      <Box bg="blackAlpha.400" py={6}>
        <Container maxW="container.xl">
          <Divider borderColor="gray.800" mb={6} />
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align="center"
            gap={4}
          >
            <Text fontSize="sm" color="gray.500" textAlign={{ base: 'center', md: 'left' }}>
              © {new Date().getFullYear()} Lake Lucien Surgery Center. All rights reserved.
            </Text>
            <Flex gap={6} fontSize="sm" flexWrap="wrap" justify={{ base: 'center', md: 'flex-end' }}>
              <Link
                as={RouterLink}
                to="/legal/hipaa-baa"
                color="gray.500"
                _hover={{ color: 'starOfLife.300' }}
                transition="all 0.3s"
              >
                HIPAA BAA
              </Link>
              <Link
                as={RouterLink}
                to="/legal/data-processing-addendum"
                color="gray.500"
                _hover={{ color: 'starOfLife.300' }}
                transition="all 0.3s"
              >
                Data Processing
              </Link>
            </Flex>
          </Flex>
          
          {/* Webmaster Credit */}
          <Box mt={4}>
            <Text fontSize="xs" color="gray.500" textAlign="center">
              Webmaster: {' '}
              <Link
                href="mailto:dev@ignacionunez.dev"
                color="gray.500"
                _hover={{ color: 'starOfLife.300' }}
                transition="all 0.3s"
              >
                dev@ignacionunez.dev
              </Link>
            </Text>
          </Box>

          {/* Developer Signature */}
          <Box mt={2}>
            <Text fontSize="xs" color="gray.500" textAlign="center">
              Developed by{' '}
              <Link
                href="https://plaintalk.dev"
                target="_blank"
                rel="noopener noreferrer"
                color="#8FE6F2"
                fontWeight="600"
                _hover={{ color: 'starOfLife.200' }}
                transition="all 0.3s"
              >
                plaintalk.dev
              </Link>
              {' '}· Ignacio Nunez
            </Text>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
